import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/site/PageHero";
import { Section, SectionHeader } from "@/components/site/Section";
import { AdvisorCTA } from "@/components/site/AdvisorCTA";
import { behaviors, dimensionList, dimensions } from "@/content/behaviors";
import { ANON_THRESHOLD } from "@/content/review";
import { SITE_URL } from "@/lib/seo";

const TITLE = "360° Leader Behavior Check | Leadership Edge Live";
const DESCRIPTION =
  "One leader, four points of view. Direct reports, peers and their manager rate the same 16 leader behaviors, and the report shows where the self-view and everyone else disagree.";

export const Route = createFileRoute("/360-leader-check")({
  head: () => ({
    links: [{ rel: "canonical", href: `${SITE_URL}/360-leader-check/` }],
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: LeaderCheckPage,
});

const steps = [
  {
    name: "You set up an account and rate yourself",
    body: "Enter your email, we send a sign-in link, no password. Then 16 behaviors, five minutes.",
  },
  {
    name: "You send private links",
    body: "One link each for direct reports, peers and your own manager. No sign-in, no accounts.",
  },
  {
    name: "You watch the counts",
    body: "You see how many people in each group have finished. You never see who.",
  },
  {
    name: "The gaps come back",
    body: "Blind spots, hidden strengths, agreed gaps, and the courses that close them.",
  },
];

function LeaderCheckPage() {
  return (
    <>
      <PageHero
        headline="Your managers don't see themselves the way their teams do."
        supporting="Sixteen behaviors, rated by the people who actually experience them. Five minutes each."
        ctas={[
          { label: "Start a check", to: "/begin-360-check/" },
          { label: "Talk to a Development Pro", to: "/contact/", variant: "secondary" },
        ]}
        maxWidth="6xl"
      />

      <Section>
        <SectionHeader
          eyebrow="How it works"
          title="Four points of view on the same 16 behaviors"
          intro="A leader rates themselves. Their direct reports, peers and their own manager rate them on the same items. The report is the difference between those views."
          titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
        />
        <div className="grid gap-5 md:grid-cols-2">
          {steps.map((step, i) => (
            <div
              key={step.name}
              className="rounded-2xl border border-border/60 bg-background p-7 transition-all hover:-translate-y-1 hover:border-primary/30 hover:shadow-brand"
            >
              <p className="text-xs font-semibold uppercase tracking-wider text-primary">
                Step {i + 1}
              </p>
              <h3 className="mt-2 font-display text-lg font-semibold text-ink">{step.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{step.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section tone="lavender">
        <SectionHeader
          eyebrow="What stays private"
          title="Nobody's individual answers reach the leader"
          intro={`Direct reports and peers are pooled. Each group needs ${ANON_THRESHOLD} answers before any of its numbers appear, and single responses are never shown.`}
          titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
        />
        <div className="grid gap-5 md:grid-cols-3">
          {[
            {
              name: "Direct reports and peers",
              body: `Pooled averages only, and only once ${ANON_THRESHOLD} people in that group have answered.`,
            },
            {
              name: "The manager",
              body: "One person, so it cannot be anonymous. Shown on its own labeled line, and they are told that up front.",
            },
            {
              name: "Everyone",
              body: "No individual answer is ever shown, at any number of responses.",
            },
          ].map((card) => (
            <div key={card.name} className="rounded-2xl border border-border/60 bg-background p-7">
              <h3 className="font-display text-lg font-semibold text-ink">{card.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{card.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeader
          eyebrow="The 16 behaviors"
          title="Four dimensions of how someone leads"
          titleClassName="text-3xl font-semibold leading-tight text-ink md:text-4xl"
        />
        <div className="grid gap-5 md:grid-cols-2">
          {dimensionList.map((slug) => (
            <div key={slug} className="rounded-2xl border border-border/60 bg-background p-7">
              <h3 className="font-display text-lg font-semibold text-ink">
                {dimensions[slug].name}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {dimensions[slug].description}
              </p>
              <ul className="mt-4 space-y-1 text-sm text-muted-foreground">
                {behaviors
                  .filter((b) => b.dimension === slug)
                  .map((b) => (
                    <li key={b.slug}>{b.name}</li>
                  ))}
              </ul>
            </div>
          ))}
        </div>
        <p className="mt-8 text-sm text-muted-foreground">
          Looking at a whole leadership group instead of one person?{" "}
          <Link to="/leadership-gaps-assessment/" className="font-semibold text-primary">
            Take the Leadership Gaps Assessment
          </Link>
          .
        </p>
      </Section>

      <AdvisorCTA
        title="The free view shows the four dimension bars. A Development Pro opens the rest."
        body="We walk through the behavior detail, the group breakdown and the courses that close the gaps, together."
        primaryLabel="Start a check"
        primaryTo="/begin-360-check/"
        secondaryLabel="Talk to a Development Pro"
        secondaryTo="/contact/"
      />
    </>
  );
}
